import { find, get, indexOf } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { WrappedFieldProps } from "redux-form";

import {
  FormError,
  FormGroup,
  FormGroupClear,
  FormGroupClearContainer,
  FormInput,
  FormLabel,
} from "src/styled";
import { GOOGLE as google } from "src/constants";
import { translate } from "src/services";

interface Props extends WrappedFieldProps {
  disabled?: boolean;
  fullWidth?: boolean;
  instantValidation?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  label?: string;
  margin?: string;
  placeholder?: string;
  readOnly?: boolean;
  setEmptyValue?: () => void;
  withPlaceholder?: boolean;
}

const LocationPickerGoogleMaps = ({
  disabled,
  fullWidth,
  input,
  instantValidation,
  isClearable,
  isLoading,
  label,
  margin,
  meta,
  meta: { dirty, asyncValidating, submitFailed, error },
  placeholder,
  readOnly,
  setEmptyValue = () => {},
  withPlaceholder,
  ...props
}: Props) => {
  const getFormattedAddress = useCallback(
    () => input.value.formattedAddress,
    [input.value.formattedAddress]
  );

  const getAddressComponent = useCallback((place: any, type: any) => {
    const addressComponent = find(
      place.address_components,
      (addressComponent) => indexOf(addressComponent.types, type) > -1
    );
    return get(addressComponent, "short_name", "");
  }, []);

  const getCity = useCallback(
    (place: any) =>
      getAddressComponent(place, "locality") ||
      getAddressComponent(place, "sublocality") ||
      getAddressComponent(place, "neighborhood") ||
      getAddressComponent(place, "administrative_area_level_3"),
    [getAddressComponent]
  );

  const getState = useCallback(
    (place: any) => getAddressComponent(place, "administrative_area_level_1"),
    [getAddressComponent]
  );

  const getCountry = useCallback(
    (place: any) => getAddressComponent(place, "country"),
    [getAddressComponent]
  );

  const getZip = useCallback(
    (place: any) => getAddressComponent(place, "postal_code"),
    [getAddressComponent]
  );

  const getStreetNumber = useCallback(
    (place: any) => getAddressComponent(place, "street_number"),
    [getAddressComponent]
  );

  const getStreet = useCallback(
    (place: any) => getAddressComponent(place, "route"),
    [getAddressComponent]
  );

  const getLocation = useCallback(
    (place: any) => ({
      city: getCity(place),
      country: getCountry(place),
      formattedAddress: place.formatted_address,
      latitude: place.geometry ? place.geometry.location.lat() : undefined,
      longitude: place.geometry ? place.geometry.location.lng() : undefined,
      state: getState(place),
      street: getStreet(place),
      streetNumber: getStreetNumber(place),
      zip: getZip(place),
    }),
    [getCity, getCountry, getState, getStreet, getStreetNumber, getZip]
  );

  const [value, setValue] = useState(getFormattedAddress());
  const locationPickerRef = useRef<HTMLInputElement>(null);

  const autoCompleteRef = useRef<any>(null);

  const disableAutocomplete = useCallback(() => {
    if (autoCompleteRef.current) {
      google.maps.event.clearInstanceListeners(autoCompleteRef);
      document.getElementsByClassName("pac-container")[0]?.remove();
    }
  }, [autoCompleteRef]);

  const enableAutocomplete = useCallback(() => {
    if (locationPickerRef.current) {
      autoCompleteRef.current = new google.maps.places.Autocomplete(locationPickerRef.current, {
        types: ["address"],
      });
      autoCompleteRef.current.addListener("place_changed", () => {
        const place = autoCompleteRef.current.getPlace();
        if (place) {
          const { formatted_address } = place;
          const location = getLocation(place);
          setValue(formatted_address || "");
          if (formatted_address) input.onChange({ ...input.value, ...location });
        }
      });
    }
  }, [getLocation, input]);

  useEffect(() => {
    if (!readOnly) enableAutocomplete();
    return () => disableAutocomplete();
  }, [disableAutocomplete, enableAutocomplete, readOnly]);

  useEffect(() => {
    if (readOnly) disableAutocomplete();
    else enableAutocomplete();
  }, [disableAutocomplete, enableAutocomplete, readOnly]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "") setEmptyValue();
    setValue(event.target.value);
  };

  const onBlur = useCallback(() => {
    setValue(getFormattedAddress() || value);
  }, [getFormattedAddress, value]);

  const clearInput = useCallback(() => {
    setValue(null);
    setEmptyValue();
    input.onChange(null);
  }, [input, setEmptyValue]);

  return (
    <FormGroup
      hasValue={value}
      isLoading={isLoading || asyncValidating}
      margin={margin}
      width={fullWidth ? "100%" : undefined}
    >
      {!!label && !placeholder && <FormLabel>{label}</FormLabel>}
      <FormGroupClearContainer>
        <FormInput
          disabled={disabled}
          onBlur={onBlur}
          onChange={onChange}
          placeholder={placeholder || (withPlaceholder ? translate("common.address") : "")}
          readOnly={readOnly}
          ref={locationPickerRef}
          type="text"
          value={value}
          rightPadding={isClearable ? "32px" : undefined}
          {...props}
        />
        {isClearable && !!value && (
          <FormGroupClear smallMargin disabled={disabled} onClick={clearInput} />
        )}
      </FormGroupClearContainer>

      {(submitFailed || (dirty && instantValidation)) && error && <FormError>{error}</FormError>}
    </FormGroup>
  );
};

export default LocationPickerGoogleMaps;
